import { useEffect, useState } from "react";
import { api, downloadXlsx } from "@/lib/api";
import { Loader2, Plus, X, Check, XCircle, FileSpreadsheet } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const STATUS = {
  pending: { bg: "#FCE7C9", color: "#9A6020", label: "Pending" },
  approved: { bg: "#D8F3DC", color: "#1B4332", label: "Approved" },
  rejected: { bg: "#FADED7", color: "#9A3412", label: "Rejected" },
};

export default function Leaves() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showApply, setShowApply] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/leaves", { params: statusFilter ? { status: statusFilter } : {} });
    setItems(data);
    setLoading(false);
  };

  useEffect(() => {
    api.get("/employees").then((r) => setEmployees(r.data));
  }, []);
  useEffect(() => { load(); }, [statusFilter]);

  const setStatus = async (id, status) => {
    await api.put(`/leaves/${id}/status`, { status });
    load();
  };

  const canApprove = user?.role !== "employee";

  return (
    <div className="space-y-6" data-testid="leaves-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="kpi-label" style={{ color: "var(--color-secondary)" }}>Time Off</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Leave Management</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => downloadXlsx("/leaves/export.xlsx", "leaves_register.xlsx",
              statusFilter ? { status: statusFilter } : undefined)}
            className="flex items-center gap-2 px-3 py-2 rounded-md text-white text-sm font-medium"
            style={{ background: "#1B4332" }} data-testid="export-leaves-xlsx">
            <FileSpreadsheet className="w-4 h-4" /> Excel
          </button>
          <button onClick={() => setShowApply(true)} data-testid="apply-leave-button"
                  className="flex items-center gap-2 px-4 py-2 rounded-md text-white font-medium active:scale-95 transition"
                  style={{ background: "var(--color-primary)" }}>
            <Plus className="w-4 h-4" /> Apply Leave
          </button>
        </div>
      </header>

      <div className="flex gap-2">
        {["", "pending", "approved", "rejected"].map((s) => (
          <button key={s || "all"} onClick={() => setStatusFilter(s)} data-testid={`filter-${s || "all"}`}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${statusFilter === s ? "bg-primary text-white" : "bg-white border border-stone-200 text-stone-700 hover:border-primary/40"}`}>
            {s ? s.charAt(0).toUpperCase() + s.slice(1) : "All"}
          </button>
        ))}
      </div>

      <div className="card-flat overflow-hidden p-0">
        {loading ? (
          <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : (
          <table className="w-full text-sm" data-testid="leaves-table">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-stone-500 bg-stone-50/60">
              <th className="px-6 py-3">Employee</th><th className="px-6 py-3">Type</th>
              <th className="px-6 py-3">From</th><th className="px-6 py-3">To</th>
              <th className="px-6 py-3">Days</th><th className="px-6 py-3">Reason</th>
              <th className="px-6 py-3">Status</th><th className="px-6 py-3"></th>
            </tr></thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan="8" className="py-10 text-center text-stone-500">No leave requests</td></tr>}
              {items.map((l) => {
                const c = STATUS[l.status];
                return (
                  <tr key={l.id} className="border-t border-stone-100">
                    <td className="px-6 py-3 font-medium">{l.employee_name}</td>
                    <td className="px-6 py-3">{l.leave_type}</td>
                    <td className="px-6 py-3 text-stone-600">{l.from_date}</td>
                    <td className="px-6 py-3 text-stone-600">{l.to_date}</td>
                    <td className="px-6 py-3">{l.days}</td>
                    <td className="px-6 py-3 text-stone-600 max-w-xs truncate">{l.reason}</td>
                    <td className="px-6 py-3"><span className="status-pill" style={{ background: c.bg, color: c.color }}>{c.label}</span></td>
                    <td className="px-6 py-3">
                      {canApprove && l.status === "pending" && (
                        <div className="flex gap-1">
                          <button onClick={() => setStatus(l.id, "approved")} data-testid={`approve-${l.id}`}
                                  className="p-1.5 rounded hover:bg-green-50 text-green-700"><Check className="w-4 h-4" /></button>
                          <button onClick={() => setStatus(l.id, "rejected")} data-testid={`reject-${l.id}`}
                                  className="p-1.5 rounded hover:bg-red-50 text-red-700"><XCircle className="w-4 h-4" /></button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showApply && <ApplyLeaveModal employees={employees} onClose={() => setShowApply(false)} onCreated={() => { setShowApply(false); load(); }} />}
    </div>
  );
}

function ApplyLeaveModal({ employees, onClose, onCreated }) {
  const [form, setForm] = useState({
    employee_id: employees[0]?.id || "", leave_type: "Casual",
    from_date: new Date().toISOString().slice(0, 10),
    to_date: new Date().toISOString().slice(0, 10), reason: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    await api.post("/leaves", form);
    setBusy(false);
    onCreated();
  };

  return (
    <div className="fixed inset-0 z-50 bg-stone-900/50 flex items-center justify-center p-4" data-testid="apply-leave-modal">
      <div className="bg-white rounded-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading text-xl font-bold">Apply for Leave</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <Sel label="Employee" value={form.employee_id} onChange={(v) => setForm({ ...form, employee_id: v })}>
            {employees.map((e) => <option key={e.id} value={e.id}>{e.full_name}</option>)}
          </Sel>
          <Sel label="Leave Type" value={form.leave_type} onChange={(v) => setForm({ ...form, leave_type: v })}>
            {["Casual", "Sick", "Earned", "Comp Off", "Maternity", "Paternity"].map((x) => <option key={x}>{x}</option>)}
          </Sel>
          <div className="grid grid-cols-2 gap-3">
            <Input label="From" type="date" value={form.from_date} onChange={(v) => setForm({ ...form, from_date: v })} />
            <Input label="To" type="date" value={form.to_date} onChange={(v) => setForm({ ...form, to_date: v })} />
          </div>
          <div>
            <div className="kpi-label mb-1.5">Reason</div>
            <textarea rows="3" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
                      className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm focus:outline-none focus:border-primary" />
          </div>
          <button type="submit" disabled={busy} data-testid="submit-leave-button"
                  className="w-full py-2 rounded-md text-white font-medium" style={{ background: "var(--color-primary)" }}>
            {busy ? "Submitting…" : "Submit Application"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Input({ label, ...props }) {
  return <label className="block"><div className="kpi-label mb-1.5">{label}</div>
    <input {...props} onChange={(e) => props.onChange(e.target.value)} className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm focus:outline-none focus:border-primary" />
  </label>;
}
function Sel({ label, value, onChange, children }) {
  return <label className="block"><div className="kpi-label mb-1.5">{label}</div>
    <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm focus:outline-none focus:border-primary">{children}</select>
  </label>;
}
